import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatISO, isValid } from 'date-fns';

import { formatDateObj, parseDate } from '@reckon-web/date-input';
import { PopoverDialog, usePopover } from '@reckon-web/popover';
import { InputSizeType, InputWeightType } from '@reckon-web/text-input';

import { Calendar, CalendarProps } from './Calendar';
import { EditableInputButton, InputButton, Placeholder } from './styled';
import { ISODate } from './types';

// Helper function to parse date strings - only accepts / separator and full yyyy format
const parseDateString = (dateString: string): Date | null => {
  const trimmed = dateString.trim();
  if (!trimmed) return null;

  // Only accept strings with exactly 2 forward slashes
  const slashCount = (trimmed.match(/\//g) || []).length;
  if (slashCount !== 2) {
    return null;
  }

  // Validate format manually before parsing
  const parts = trimmed.split('/');
  if (parts.length !== 3) {
    return null;
  }

  const [dayStr, monthStr, yearStr] = parts;

  // Strict validation: must be exactly dd/MM/yyyy format
  if (dayStr.length !== 2 || monthStr.length !== 2 || yearStr.length !== 4) {
    return null;
  }

  // Check if all parts are numbers
  if (
    !/^\d{2}$/.test(dayStr) ||
    !/^\d{2}$/.test(monthStr) ||
    !/^\d{4}$/.test(yearStr)
  ) {
    return null;
  }

  // Only accept dd/MM/yyyy format (full 4-digit year)
  try {
    const parsed = parseDate(trimmed, 'dd/MM/yyyy');
    if (isValid(parsed)) {
      return parsed;
    }
  } catch {
    // Invalid format
  }

  return null;
};

export type DatePickerProps = {
  /** When true, the trigger will be disabled and the clear button is removed. */
  disabled?: boolean;
  /** Indicate that the value does not conform to the format expected by the application. */
  invalid?: boolean;
  /** Called when the value changes. */
  onChange: (value: ISODate | undefined) => void;
  /** Called when the value is cleared. */
  onClear: () => void;
  /** The value of the calendar, displayed in the trigger. */
  value: ISODate | undefined;
  size?: InputSizeType;
  weight?: InputWeightType;
  /** When true, allows direct text input editing */
  editable?: boolean;
  /** Placeholder text for editable input */
  placeholder?: string;
} & Pick<
  CalendarProps,
  'disabled' | 'fromMonth' | 'month' | 'modifiers' | 'toMonth'
>;

export const DatePicker = ({
  onChange,
  onClear,
  value,
  // calendar props
  disabled: disabledDays,
  fromMonth,
  month: initialMonth,
  modifiers,
  toMonth,
  size = 'medium',
  weight = 'subtle',
  editable = true,
  placeholder = 'dd/mm/yyyy',
  ...buttonProps
}: DatePickerProps) => {
  const [calendarMonth, setCalendarMonth] = useState<Date>(
    value ? new Date(value) : new Date()
  );

  // Track input text separately from value for editing state
  const [inputText, setInputText] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [inputInvalid, setInputInvalid] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLElement>(null);

  const { isOpen, setOpen, dialog, trigger } = usePopover({
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  // Handle clicks outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isOpen, setOpen]);

  const calendarProps = {
    disabled: disabledDays,
    fromMonth,
    modifiers,
    toMonth,
  };

  const handleCalendarClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleDayClick = useCallback(
    (day: Date, modifiers: any) => {
      if (modifiers.disabled) {
        return;
      }
      const isoDate = formatISO(day, { representation: 'date' }) as ISODate;
      onChange(isoDate);

      // Update input text to match selected date
      if (editable) {
        setInputText(formatDateObj(day));
        setIsEditing(false);
        setInputInvalid(false);
      }

      // Brief delay so user can see the selection
      setTimeout(handleCalendarClose, 300);
    },
    [onChange, editable, handleCalendarClose]
  );

  // Handle calendar month navigation (completely independent)
  const handleMonthChange = useCallback((newMonth: Date) => {
    setCalendarMonth(newMonth);
  }, []);

  // Ensure selectedDay is properly calculated and valid
  const selectedDay = useMemo(() => {
    if (!value) return undefined;
    try {
      const date = new Date(value as string);
      const isValidDate = isValid(date);
      return isValidDate ? date : undefined;
    } catch {
      return undefined;
    }
  }, [value]);
  const formattedDate = value ? formatDateObj(new Date(value)) : undefined;

  // Sync input text with formatted date when value changes externally
  useEffect(() => {
    if (!isEditing) {
      setInputText(formattedDate || '');
      setInputInvalid(false);
    }
  }, [formattedDate, isEditing]);

  // Handle input text changes
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newText = event.target.value;
      setInputText(newText);
      setIsEditing(true);

      // Try to parse the input as a date
      if (newText.trim() === '') {
        setInputInvalid(false);
        onChange(undefined); // Clear selection when input is empty
        return;
      }

      const parsedDate = parseDateString(newText);
      if (parsedDate && isValid(parsedDate)) {
        setInputInvalid(false);
        // Only call onChange when we have a complete, valid date
        const isoDate = formatISO(parsedDate, {
          representation: 'date',
        }) as ISODate;
        onChange(isoDate);

        // Only update calendar month if we have a complete valid date AND we're actively typing
        // Check if the input looks complete (has proper date format with 4-digit year)
        const parts = newText.split('/');
        if (parts.length === 3 && parts[2].length === 4) {
          setCalendarMonth(parsedDate);
        }
      } else {
        setInputInvalid(true);
        // Clear the selection when date becomes invalid
        onChange(undefined);
      }
    },
    [onChange]
  );

  // Handle input blur - finalize editing state
  const handleInputBlur = useCallback(() => {
    setIsEditing(false);

    // If input is invalid or empty, revert to the current value
    if (inputInvalid || inputText.trim() === '') {
      if (value) {
        setInputText(formatDateObj(new Date(value)));
        setInputInvalid(false);
      } else {
        setInputText('');
        onClear();
      }
    }
  }, [inputInvalid, inputText, value, onClear]);

  // Handle input key events
  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        inputRef.current?.blur();
      } else if (event.key === 'Escape') {
        // Revert to original value
        setInputText(formattedDate || '');
        setInputInvalid(false);
        setIsEditing(false);
        inputRef.current?.blur();
      }
    },
    [formattedDate]
  );

  // Handle clear action
  const handleClear = useCallback(() => {
    onClear();
    setInputText('');
    setInputInvalid(false);
    setIsEditing(false);
  }, [onClear]);

  // Handle button/input click
  const handleTriggerClick = useCallback(() => {
    if (editable) {
      inputRef.current?.focus();
    } else {
      setOpen(true);
    }
  }, [editable, setOpen]);

  // Handle calendar icon click
  const handleCalendarIconClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(!isOpen);
    },
    [isOpen, setOpen]
  );

  // Handle input focus - open calendar and set calendar month to selected date if there is one
  const handleInputFocus = useCallback(() => {
    if (editable) {
      setOpen(true);
      // When opening the calendar, show the month of the current value if there is one
      if (value && selectedDay) {
        setCalendarMonth(selectedDay);
      }
    }
  }, [editable, setOpen, value, selectedDay]);

  // Determine if we should show invalid state
  const shouldShowInvalid = buttonProps.invalid || (editable && inputInvalid);

  const ariaLabel =
    'Choose date' +
    (formattedDate ? `, selected date is ${formattedDate}` : '');

  return (
    <Fragment>
      {editable ? (
        <div
          ref={(el) => {
            trigger.ref(el as any);
            triggerRef.current = el;
          }}
          {...trigger.props}
        >
          <EditableInputButton
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            onClear={value ? handleClear : undefined}
            onCalendarIconClick={handleCalendarIconClick}
            placeholder={placeholder}
            invalid={shouldShowInvalid}
            isSelected={isOpen}
            size={size}
            weight={weight}
            aria-label={ariaLabel}
            {...buttonProps}
          />
        </div>
      ) : (
        <InputButton
          aria-label={ariaLabel}
          onClick={handleTriggerClick}
          onClear={value ? handleClear : undefined}
          isSelected={isOpen}
          ref={(el) => {
            trigger.ref(el as any);
            triggerRef.current = el;
          }}
          size={size}
          weight={weight}
          invalid={shouldShowInvalid}
          {...buttonProps}
          {...trigger.props}
        >
          {formattedDate || <Placeholder>{placeholder}</Placeholder>}
        </InputButton>
      )}

      <PopoverDialog
        isVisible={isOpen}
        ref={(el) => {
          dialog.ref(el as any);
          popoverRef.current = el;
        }}
        {...dialog.props}
      >
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Calendar
            onDayClick={handleDayClick}
            selected={selectedDay}
            month={calendarMonth}
            onMonthChange={handleMonthChange}
            defaultMonth={initialMonth}
            mode="single"
            key={selectedDay ? selectedDay.getTime() : 'no-selection'} // Force re-render when selection changes
            {...calendarProps}
          />
        </div>
      </PopoverDialog>
    </Fragment>
  );
};
